import NewLayout from "@components/new-ui/Layout";
import React from "react";
import { CasestudyCard, CasestudyCardProps } from ".";
import { CaseStudyHerosection, CaseStudyInformation, FeatureList, Navbar, navLinks, OrganizationProfile } from "./alrannan-administrative-services";
import StatsInfo from "@components/new-ui/IndustriesSection/StatsInfo";

const casestudyCardsDetails: CasestudyCardProps[] = [
    {
        imageUrl: "https://assets.gallabox.com/gb-home/case-study/inlingua/inlingua.png",
        title: "Inlingua scales student engagement and doubles growth with Gallabox",
        sector: "Education",
        externalLink: "/case-study/inlingua",
    },
    {
        imageUrl: "https://assets.gallabox.com/gb-home/case-study/khivraj-elixir/khivraj-elixir.png",
        title: "Khivraj Elixir accelerates lead conversion with Gallabox",
        sector: "Automobiles",
        externalLink: "/case-study/khivraj-elixir",
    },
    {
        imageUrl: "https://assets.gallabox.com/gb-home/case-study/nura/nura-hero.png",
        title: "How NURA Ai Health Screening Simplified Operations with Gallabox",
        sector: "Healthcare",
        externalLink: "/case-study/nura",
    },
];

const TilLimited: React.FC = () => {
    return (
        <NewLayout
            title="TIL Limited strengthens customer engagement and drives growth with Gallabox | Case Study"
            description="Discover how TIL Limited, a leader in material handling and infrastructure equipment, increased lead generation 5x, improved employee productivity 10x, and cut marketing costs 5x using Gallabox's automated WhatsApp communication tools."
            canonical="https://gallabox.com/case-study/til-limited"
            ogAttributes={{ image: "https://assets.gallabox.com/gb-home/case-study/til-limited/til-limited-og.png" }}
        >
            <CaseStudyHerosection
                title="TIL Limited boosts lead generation and customer engagement with Gallabox"
                imageUrl={"https://assets.gallabox.com/gb-home/case-study/til-limited/til-limited.png"}
                redirectUrl={"/case-study"}
            />
            <StatsInfo
                statsInfo={[
                    { percentage: "2x", label: "overall business growth" },
                    { percentage: "50%", label: "increase in lead generation" },
                    { percentage: "50%", label: "reduction in marketing spend" },
                ]}
            />

            <section className="max-w-7xl mx-auto  md:grid md:grid-cols-6 mt-24 mb-24">
                <div className="bg-white col-span-1">
                    <div style={{ position: "sticky", top: "100px" }}>
                        <OrganizationProfile
                            companyProfileData={[{ label: "Category", value: ["Manufacturing", "Infrastructure"] }]}
                            product={{
                                title: "Features Used",
                                productLink: [
                                    { link: "https://gallabox.com/whatsapp-chatbot", label: "Chatbots" },
                                    { link: "https://gallabox.com/whatsapp-broadcast", label: "Broadcast" },
                                ],
                            }}
                        />
                    </div>
                </div>
                <div className=" col-span-4 md:pl-8 px-4">
                    <div className="flex flex-col gap-y-12 ">
                        <section id="About TIL Limited">
                            <CaseStudyInformation subTitle={"About TIL Limited"} className="mt-0" />
                            <p className="mt-2">
                                TIL Limited has been a cornerstone of India&apos;s material handling and infrastructure landscape since 1944. Known for building tough, reliable
                                equipment that powers major projects across ports, railways, mining, and construction, TIL combines skilled craftsmanship with cutting-edge R&D.
                                Headquartered in Kolkata with offices nationwide, it&apos;s a company proud of its heritage and its role in India&apos;s growth story.
                            </p>
                        </section>

                        <section id="Challenges">
                            <CaseStudyInformation subTitle={"Challenges before Gallabox"} className="mt-0" />

                            <p className="mt-2">
                                As India&apos;s infrastructure needs grew, so did TIL&apos;s customer inquiries. But the company faced mounting hurdles when it came to managing
                                communication at scale:
                            </p>
                            <FeatureList
                                List={[
                                    {
                                        listItem: "Reaching customers quickly and consistently",
                                        nestedListItem: [
                                            "Communication relied heavily on phone calls, emailers, and physical visits. While personal, these methods were time-consuming and often delayed responses to potential buyers.",
                                        ],
                                    },
                                    {
                                        listItem: "Fragmented outreach",
                                        nestedListItem: [
                                            "Cold calls and social media messaging lacked structure, making it difficult to nurture prospects effectively or track conversations across teams.",
                                        ],
                                    },
                                    {
                                        listItem: "Strain on team bandwidth",
                                        nestedListItem: [
                                            "Sales teams were stretched thin, juggling follow-ups and repetitive queries instead of focusing on high-value opportunities.",
                                        ],
                                    },
                                ]}
                                className="mt-4 bg-gray-100"
                            />
                            <p className="mt-5">Without a scalable system in place, TIL risked losing valuable leads simply because they couldn&apos;t be engaged fast enough.</p>
                            <CaseStudyInformation subTitle={"Initial solutions tried"} className="mt-12" />
                            <p className="mt-2">Before Gallabox, TIL tried to keep up using:</p>
                            <FeatureList
                                List={[
                                    {
                                        listItem: "One-to-one outreach via calls, emails, and social media.",
                                    },
                                    {
                                        listItem: "Traditional cold calling and physical visits to prospects.",
                                    },
                                ]}
                                className="mt-4 bg-gray-100"
                            />
                            <p className="mt-5">
                                These approaches worked to an extent but were manual-heavy, inconsistent, and costly. The lack of automation meant the team spent more time on
                                repetitive tasks than on closing deals.
                            </p>
                        </section>

                        <section id="Solutions">
                            <CaseStudyInformation subTitle={"Why TIL chose Gallabox"} className="mt-0" />
                            <p className="mt-2">The turning point came when TIL discovered Gallabox. Unlike traditional outreach, Gallabox provided:</p>

                            <FeatureList
                                List={[
                                    {
                                        listItem: "Bulk messaging to instantly connect with a large customer base.",
                                    },
                                    {
                                        listItem: "Chatbots and broadcasts that streamlined lead capture and customer support.",
                                    },
                                    {
                                        listItem: "Automation that felt human, ensuring conversations stayed warm while cutting down manual work.",
                                    },
                                ]}
                                className="mt-4 bg-gray-100"
                            />
                            <p className="mt-5">
                                For a company operating across multiple industries and regions, Gallabox offered the scalability and reliability TIL needed to keep pace with its
                                customers.
                            </p>
                            <CaseStudyInformation subTitle={"Goals with Gallabox"} className="mt-12" />
                            <p className="mt-2">With Gallabox in place, TIL aimed to:</p>
                            <FeatureList
                                List={[
                                    { listItem: "Generate leads more efficiently" },
                                    { listItem: "Streamline customer communication on one platform" },
                                    { listItem: "Automate repetitive support queries and follow-ups" },
                                    { listItem: "Free up teams to focus on relationship-building and sales" },
                                ]}
                                className="mt-4 bg-gray-100"
                            />
                        </section>

                        <section id="Impact">
                            <CaseStudyInformation subTitle={"Impact on business growth"} className="mt-0" />
                            <p className="mt-2">The results were immediate and measurable:</p>

                            <FeatureList
                                List={[
                                    {
                                        listItem: "5x increase in lead generation, thanks to instant engagement on WhatsApp.",
                                    },
                                    {
                                        listItem: "10x boost in employee productivity, with teams no longer tied up in repetitive follow-ups.",
                                    },
                                    {
                                        listItem: "5x reduction in marketing and advertising spend, with higher ROI from automated campaigns.",
                                    },
                                    {
                                        listItem: "20% overall business growth, proving that better communication directly drives scale.",
                                    },
                                ]}
                                className="mt-5 bg-gray-100"
                            />

                            <p className="mt-5">
                                Gallabox helped TIL turn conversations into conversions, giving its sales teams the edge they needed to win in a competitive market.
                            </p>

                            <CaseStudyInformation subTitle={"Turning conversations into long-term partnerships"} className="mt-12" />
                            <p className="mt-2">
                                In the manufacturing and infrastructure sector, relationships matter as much as products. Customers expect clear communication, quick responses, and
                                consistent support. By adopting Gallabox, TIL was able to deliver exactly that — without overburdening its teams.
                            </p>
                            <p className="mt-5">
                                What once required hours of manual follow-ups now happens in minutes. This efficiency not only saved time and costs but also strengthened trust with
                                customers across industries.
                            </p>
                            <CaseStudyInformation subTitle={"Looking forward"} className="mt-10" />
                            <p className="mt-2">
                                TIL now sees Gallabox as a long-term partner in its growth journey. The company plans to deepen automation across customer support, personalize
                                onboarding journeys for clients, and scale communication as demand increases.
                            </p>
                            <p className="mt-5">
                                With Gallabox powering its conversations, TIL is set to keep building stronger customer relationships while driving India&apos;s infrastructure
                                story forward.
                            </p>
                        </section>
                    </div>
                </div>
                <div>
                    <div style={{ position: "sticky", top: "100px", minHeight: "510px" }} className="hidden md:block">
                        <Navbar links={navLinks} />
                    </div>
                </div>
            </section>
            <div className="max-w-6xl mx-auto">
                <section>
                    <h3 className="text-gray-900 mb-10 mt-5 text-left ml-4">Check out other customer stories</h3>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 max-w-6xl mx-auto gap-10 mb-20">
                        {casestudyCardsDetails?.map((data, index) => (
                            <CasestudyCard key={index} {...data} />
                        ))}
                    </div>
                </section>
            </div>
        </NewLayout>
    );
};

export default TilLimited;
